import _ from "lodash";
import { LngLat } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from "react";
import { useParams } from 'react-router-dom';
import { useLeadStore, usePipedriveStore } from "../..";
import useCurrentOrFallbackPipedriveUserId from "../../hooks/useCurrentOrFallbackPipedriveUserId";
import useProperty from "../../hooks/useProperty";
import { DealState, DealStatus } from "../../interfaces/IDeal";
import { formatAddress } from "../../utils/addressFormat";
import { formatDateTime } from "../../utils/utils";
import OnMapSpinner from "../spinners/OnMapSpinner";
import DealCreate from "./DealCreate";

function DealCreateFromLead() {
    const { leadId } = useParams();
    const leadStore = useLeadStore();
    const pipedriveStore = usePipedriveStore();
    const lead = useMemo(() => computed(() => leadStore.leads.find(l => l.id === Number(leadId))), [leadId]).get();
    const coordinates = useMemo(() => lead && new LngLat(lead.centerPoint.longitude, Number(lead.centerPoint.latitude)), [lead]);
    const { property, isLoading, fetchProperty } = useProperty(coordinates);
    const pipedriveUserId = useCurrentOrFallbackPipedriveUserId();
    const initialState = useMemo(() => computed(() => property && lead ? {
        ...DealState.empty(),
        name: formatAddress({ address: property.address, city: property.city, state: property.state }),
        propertyOwner: lead.contactName || lead.ownerName,
        zoning: lead.zoning,
        buildingSize: lead.buildingSize,
        landSizeAcres: lead.landSize ? Number(lead.landSize.toFixed(2)) : null,
        notes: _.orderBy(lead.notes, n => n.createdAt, "desc").map(n => `${formatDateTime(n.createdAt)} (${n.userName})\n${n.text}`).join("\n\n") || null,

        address: property.address,
        city: property.city,
        county: property.county,
        state: property.state,

        assignedToUserId: pipedriveUserId,
        dealShapeApns: [lead.parcelId],
        parcelId: lead.parcelId,
        status: DealStatus.Open,
        dealStageId: pipedriveStore.dealStages.find(s => s.name === "Pursuit")?.id || null,
    } : null), [property, lead, pipedriveUserId]).get();

    useEffect(fetchProperty, [coordinates]);

    if (isLoading || leadStore.isLeadsLoading.fetching)
        return <OnMapSpinner />;

    if (!property || !lead)
        return null;

    return (
        <DealCreate
            initialState={initialState!}
            latitude={lead.centerPoint.latitude}
            longitude={lead.centerPoint.longitude}
            shapes={property.shapes}
        />
    );
}

export default observer(DealCreateFromLead);
